import React from "react";
import Header from "../../components/Header/Header";
import Layout from "../../components/Layout/Layout";
import { Link } from "react-router-dom";

const StyleGuide: React.FC = () => {
    return (
        <Layout
            Header={(): React.ReactNode => (
                <Header>
                    <h1>Style Guide [H1]</h1>
                </Header>
            )}>
            <article className="layout-content-container">
                <div>
                    <h2>Quick Access</h2>
                    <ul>
                        <li>
                            <Link to="/styleguide/icon-library">Icon Library</Link>
                        </li>
                        <li>
                            <Link to="/styleguide/quote-table">Quote Table</Link>
                        </li>
                        <li>
                            <Link to="/styleguide/components">Components</Link>
                        </li>
                    </ul>
                </div>
                <div>
                    <p>
                        This page exists as a catalogue of common markup patterns and
                        their associated styles on this site [p.text-large].
                    </p>
                    <p>
                        With possibly a few exceptions, all pages begin with an{" "}
                        <code>&lt;h1&gt;</code> element, which is exclusively used for the
                        page&apos;s main title. No page should have more than one{" "}
                        <code>&lt;h1&gt;</code> element.
                    </p>

                    <h2>Document Structure [H2]</h2>

                    <p>
                        In order to make sense for screen readers, the headings should be
                        structured according to the hierarchy of content. Typically,
                        section and subsection headings will use <code>&lt;h2&gt;</code>{" "}
                        and <code>&lt;h3&gt;</code> HTML tags respectively. In rare cases,
                        headings may extend up to 6 levels deep.
                    </p>
                    <p>
                        Some components, e.g. sidebar / &apos;aside&apos; panels may have
                        headings and paragraph or list content. The headings in these
                        components should be an <code>&lt;h4&gt;</code> (or deeper level)
                        for two reasons:
                    </p>
                    <ol>
                        <li>
                            These are separate from the main content, and therefore
                            provide auxiliary information;
                        </li>
                        <li>
                            The pages using such components are not expected to be
                            lengthy, deeply-hierarchical pages such as legal terms and
                            policies.
                        </li>
                    </ol>
                    <p>A sample of such components may be provided later.</p>

                    <h3>Sub-hierarchy Example [H3]</h3>

                    <h4>Level-4 Heading</h4>
                    <p>
                        While <code>&lt;h4&gt;</code> elements may be typically used for
                        page components that are auxiliary to the main content, they may
                        also exist in the core content, e.g. for legal copy.
                    </p>

                    <h5>Level-5 Heading</h5>
                    <p>
                        Usage of headings at this level would be very rare, unless as a
                        subheading in auxiliary content.
                    </p>

                    <h6>Level-6 Heading</h6>
                    <p>
                        Who uses an <code>&lt;h6&gt;</code> element? Seriously?
                    </p>

                    <h2>Rich Text Formatting</h2>

                    <p>
                        Several options exist for formatting key words and phrases within
                        paragraph text. The most commonly-used formatting is provided by
                        native HTML elements, particularly <a href="#">hyperlinks</a>, the{" "}
                        <code>&lt;strong&gt;</code> tag for <strong>bold text</strong>,{" "}
                        <code>&lt;em&gt;</code> for <em>emphasised text</em> that
                        typically uses italics, and <code>&lt;code&gt;</code> for special
                        technical terms, the names of specific operations or commands, or
                        something intended for the reader to type in somewhere.
                    </p>
                    <p>
                        Additional inline formatting options may be provided by the use of{" "}
                        <code>&lt;span&gt;</code> tags, each with a particular CSS class
                        defined. Presently, no such classes have been defined.
                    </p>

                    <h3>Lists</h3>

                    <p>
                        Apart from paragraphs (using the HTML <code>&lt;p&gt;</code>{" "}
                        element), content can be presented in lists, as well as images or
                        figures. This section demonstrates the use of ordered (i.e.
                        numbered) and unordered lists, using <code>&lt;ol&gt;</code> and{" "}
                        <code>&lt;ul&gt;</code> elements respectively. Note that inner
                        lists may be of either type, e.g. an unordered list inside an
                        ordered list and vice-versa.
                    </p>
                    <p>
                        Here is the unordered list, showing nesting up to 3 levels deep:
                    </p>
                    <ul>
                        <li>First item</li>
                        <li>Second item</li>
                        <li>
                            Third item, which contains a nested list:
                            <ul>
                                <li>Sub-item #1</li>
                                <li>Sub-item #2</li>
                                <li>
                                    <p>
                                        Sub-item #3, which contains yet another list for
                                        the fun of it:
                                    </p>
                                    <ul>
                                        <li>Detailed point #1</li>
                                        <li>Detailed point #2</li>
                                        <li>Detailed point #3</li>
                                    </ul>
                                </li>
                            </ul>
                        </li>
                    </ul>
                    <p>Here is the ordered list, showing nesting up to 3 levels deep:</p>
                    <ol>
                        <li>First item</li>
                        <li>Second item</li>
                        <li>
                            Third item, which contains a nested list:
                            <ol>
                                <li>Sub-item #1</li>
                                <li>Sub-item #2</li>
                                <li>
                                    <p>
                                        Sub-item #3, which contains yet another list for
                                        the fun of it:
                                    </p>
                                    <ol>
                                        <li>Detailed point #1</li>
                                        <li>Detailed point #2</li>
                                        <li>Detailed point #3</li>
                                    </ol>
                                </li>
                            </ol>
                        </li>
                    </ol>
                </div>
            </article>
        </Layout>
    );
};

export default StyleGuide;
